import { coachPlanSchema, type SavedCoachPlan } from "@reactive-resume/schema";
import _set from "lodash.set";

import type { BuildCoachPlanResult } from "./build-coach-plan";
import { sumGains } from "./coach-attributed-score";
import { applySkillAdditions } from "./coach-skill-additions";

// Stable across object key order, regenerated item ids and presentation-only metadata.
const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, entry]) => key !== "id" && entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalize(entry)]),
  );
};

export const coachResumeContentKey = (resumeData: unknown): string => {
  const document = resumeData as { basics?: unknown; sections?: unknown };
  return JSON.stringify(canonicalize({ basics: document.basics, sections: document.sections }));
};

export type CoachPlanContext = {
  jobUuid: string;
  jobDescription: string;
  baselineScore: number;
  baselineRoleDomainFit: number;
  baselineSkillOverlap: number;
  fixedContribution: number;
  weights: Record<string, number>;
  projectedCeiling: number;
};

const contextKey = (context: CoachPlanContext) =>
  JSON.stringify(
    canonicalize({
      jobUuid: context.jobUuid,
      jobDescription: context.jobDescription,
      baselineScore: context.baselineScore,
      baselineRoleDomainFit: context.baselineRoleDomainFit,
      baselineSkillOverlap: context.baselineSkillOverlap,
      fixedContribution: context.fixedContribution,
      weights: context.weights,
      projectedCeiling: context.projectedCeiling,
    }),
  );

export const saveCoachPlan = (
  context: CoachPlanContext,
  resumeData: unknown,
  plan: BuildCoachPlanResult,
): SavedCoachPlan => ({
  version: 1,
  contextKey: contextKey(context),
  contentKey: coachResumeContentKey(resumeData),
  plan,
});

export const restoreCoachPlan = (
  snapshot: unknown,
  context: CoachPlanContext,
  resumeData: unknown,
): BuildCoachPlanResult | null => {
  const parsed = coachPlanSchema.safeParse(snapshot);
  if (!parsed.success) return null;
  const saved = parsed.data;
  if (saved.contextKey !== contextKey(context)) return null;
  if (saved.contentKey !== coachResumeContentKey(resumeData)) return null;
  return saved.plan;
};

/** Bank accepted credit once, retaining the original gains of all remaining cards. */
export const continueCoachPlan = ({
  snapshot,
  context,
  resumeData,
  acceptedIds,
  score,
}: {
  snapshot: unknown;
  context: CoachPlanContext;
  resumeData: unknown;
  acceptedIds: string[];
  score: number;
}): SavedCoachPlan | null => {
  const parsed = coachPlanSchema.safeParse(snapshot);
  if (!parsed.success || parsed.data.contextKey !== contextKey(context)) return null;
  const plan = parsed.data.plan;
  const accepted = new Set(acceptedIds);
  // Only ordinary accepts can carry the assessment forward. Manual edits require a
  // fresh plan; storing their fingerprint under the old plan would hide stale advice.
  let expected: Record<string, unknown>;
  try {
    expected = JSON.parse(parsed.data.contentKey) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!expected || typeof expected !== "object" || Array.isArray(expected)) return null;
  for (const suggestion of plan.suggestions) {
    if (accepted.has(suggestion.id)) _set(expected, suggestion.fieldPath, suggestion.suggestedText);
  }
  expected = applySkillAdditions(
    expected,
    plan.skillAdditions.filter(({ id }) => accepted.has(id)),
  );
  if (coachResumeContentKey(expected) !== coachResumeContentKey(resumeData)) return null;
  const applied = sumGains(plan.gains, [...accepted]);
  return saveCoachPlan(
    {
      ...context,
      baselineScore: score,
      baselineSkillOverlap:
        Math.round(Math.min(100, context.baselineSkillOverlap + applied.skillOverlap) * 100) / 100,
    },
    resumeData,
    {
      ...plan,
      suggestions: plan.suggestions.filter(({ id }) => !accepted.has(id)),
      skillAdditions: plan.skillAdditions.filter(({ id }) => !accepted.has(id)),
      gains: Object.fromEntries(Object.entries(plan.gains).filter(([id]) => !accepted.has(id))),
      originalValues: Object.fromEntries(
        plan.suggestions
          .filter(({ id }) => !accepted.has(id))
          .map(({ fieldPath }) => [fieldPath, plan.originalValues[fieldPath]]),
      ),
    },
  );
};
